import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GlobalQueryKeys } from '@enums/react-query-keys';
import { usePreferences } from '@context/preferences.context';
import { useShoppingPreferences } from '@hooks/user/use-shopping-preferences';
import { getStickyNavData } from '@api/static';
import { StickyNavApiResponse, StickyNavApiEntry } from '@interfaces/models/sticky-nav';
import { MEN_UNIVERSE } from '@constants/user-shopping-preferences';

interface UseStickyNavQueryParams {
  universe?: number;
}

const useStickyNavQuery = ({ universe }: UseStickyNavQueryParams = {}) => {
  const { siteId, locale } = usePreferences();
  const { preferences, sessionPrefs } = useShoppingPreferences();
  const queryClient = useQueryClient();

  const currentUniverse = universe ?? sessionPrefs?.universe ?? preferences?.universe ?? 1;

  const cachedStickyNavData = queryClient.getQueryData<StickyNavApiResponse>([GlobalQueryKeys.STICKY_NAV]);
  const shouldFetch = !cachedStickyNavData;

  const { data: apiResponse } = useQuery<StickyNavApiResponse>({
    queryFn: () => getStickyNavData({ siteId, locale }),
    queryKey: [GlobalQueryKeys.STICKY_NAV],
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled: shouldFetch,
    initialData: cachedStickyNavData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const stickyNavData = useMemo<StickyNavApiEntry[]>(() => {
    if (!apiResponse && !cachedStickyNavData) {
      return [];
    }
    const dataToUse = apiResponse || cachedStickyNavData;
    const result = currentUniverse === MEN_UNIVERSE ? dataToUse.men : dataToUse.women;
    return result;
  }, [currentUniverse, apiResponse, cachedStickyNavData]);

  return {
    stickyNavData,
    isLoading: shouldFetch && !apiResponse && !cachedStickyNavData,
  };
};

export default useStickyNavQuery;
